/**
 * Location List Component
 *
 * Virtualized scrollable list of location cards.
 * Only renders items visible in the viewport + buffer for smooth scrolling.
 * Subscribes to locationStore for visible locations and selection state.
 *
 * @element peach-locations-list
 *
 * @remarks
 * Virtualization prevents DOM bloat when displaying hundreds of locations.
 * Uses fixed item height estimation for scroll calculations.
 *
 * @example
 * ```html
 * <peach-locations-list></peach-locations-list>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { locationStore } from "@/features/locations/stores/location-store";
import type { Location } from "@/types/location";
import "./locations-list-item";

/**
 * Estimated height of each location card in pixels.
 * Used for virtual scroll calculations.
 */
const ITEM_HEIGHT = 140;

/**
 * Number of items to render above/below the visible area.
 * Provides smooth scrolling experience.
 */
const BUFFER_ITEMS = 5;

@customElement("peach-locations-list")
export class LocationsListComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Visible locations from store (all of them, for virtual scroll)
   */
  @state()
  declare private locations: Location[];

  /**
   * Sorted locations cache (avoid re-sorting on every scroll)
   */
  private sortedLocationsCache: Location[] = [];
  private locationsCacheKey: string = "";

  /**
   * Selected location ID
   */
  @state()
  declare private selectedLocationId: number | null;

  /**
   * Loading state
   */
  @state()
  declare private isLoading: boolean;

  /**
   * Virtual scroll state - which items are currently rendered
   */
  @state()
  declare private virtualStart: number;

  @state()
  declare private virtualEnd: number;

  /**
   * Reference to the scroll container
   */
  @query(".location-list-scroll-container")
  private scrollContainer?: HTMLElement;

  /**
   * Store unsubscribe function
   */
  private unsubscribe?: () => void;

  /**
   * Scroll handler (bound for cleanup)
   */
  private boundScrollHandler: () => void;

  /**
   * Pending scroll update (for throttling)
   */
  private scrollUpdatePending = false;

  constructor() {
    super();
    this.locations = [];
    this.selectedLocationId = null;
    this.isLoading = true;
    this.virtualStart = 0;
    this.virtualEnd = 20; // Initial render count
    this.boundScrollHandler = this.handleScroll.bind(this);
  }

  /**
   * Subscribe to location store on connect
   *
   * @remarks
   * Subscribes to visibleLocationIds Set for efficient change detection.
   * Resets virtual scroll position when locations change.
   */
  connectedCallback() {
    super.connectedCallback();

    // Set host element styles for proper layout (light DOM mode)
    this.style.display = "block";
    this.style.height = "100%";

    // Subscribe to location store
    this.unsubscribe = locationStore.subscribe(
      (state) => ({
        visibleLocationIds: state.visibleLocationIds,
        selectedLocationId: state.selectedLocationId,
        isLoading: state.isLoading,
      }),
      (selected) => {
        // Get visible locations
        const visibleLocations = locationStore.getState().getVisibleLocations();

        // Check if locations actually changed (not just selection)
        const locationsChanged = visibleLocations.length !== this.locations.length ||
          visibleLocations.some((loc, i) => loc.id !== this.locations[i]?.id);

        this.locations = visibleLocations;
        this.selectedLocationId = selected.selectedLocationId;

        // Clear sort cache when locations change
        if (locationsChanged) {
          this.locationsCacheKey = "";
          // Reset virtual scroll to top when locations change
          this.virtualStart = 0;
          this.virtualEnd = Math.min(20, visibleLocations.length);
        }

        // Set loading state
        const allLocations = locationStore.getState().getAllLocations();
        this.isLoading = allLocations.length === 0 && selected.isLoading;

        // Auto-scroll to selected location
        if (selected.selectedLocationId !== null) {
          this.scrollToSelected(selected.selectedLocationId);
        }
      },
      {
        equalityFn: (a, b) =>
          a.visibleLocationIds.size === b.visibleLocationIds.size &&
          a.isLoading === b.isLoading &&
          a.selectedLocationId === b.selectedLocationId &&
          Array.from(a.visibleLocationIds).every((id) => b.visibleLocationIds.has(id)),
        fireImmediately: true,
      },
    );

    // Initialize from current state
    const state = locationStore.getState();
    this.locations = state.getVisibleLocations();
    this.selectedLocationId = state.selectedLocationId;
    this.isLoading = state.getAllLocations().length === 0;
  }

  /**
   * Track the element we've attached scroll listener to
   */
  private currentScrollElement: HTMLElement | null = null;

  /**
   * Get scroll container element
   *
   * @remarks
   * Uses @query decorator with fallback to querySelector for light DOM compatibility.
   */
  private getScrollContainer(): HTMLElement | null {
    return this.scrollContainer ?? this.querySelector(".location-list-scroll-container");
  }

  /**
   * Attach scroll listener when scroll container is available
   */
  protected updated() {
    const container = this.getScrollContainer();

    // Check if we need to attach/re-attach scroll listener
    if (container && container !== this.currentScrollElement) {
      // Remove from old element if exists
      this.currentScrollElement?.removeEventListener("scroll", this.boundScrollHandler);

      // Attach to new element
      container.addEventListener("scroll", this.boundScrollHandler, { passive: true });
      this.currentScrollElement = container;

      // Wait for layout before calculating bounds
      requestAnimationFrame(() => this.updateVirtualBounds());
    }
  }

  /**
   * Clean up on disconnect
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.currentScrollElement?.removeEventListener("scroll", this.boundScrollHandler);
    this.currentScrollElement = null;
  }

  /**
   * Handle scroll events with throttling
   */
  private handleScroll() {
    if (this.scrollUpdatePending) return;

    this.scrollUpdatePending = true;
    requestAnimationFrame(() => {
      this.updateVirtualBounds();
      this.scrollUpdatePending = false;
    });
  }

  /**
   * Calculate which items should be rendered based on scroll position
   */
  private updateVirtualBounds() {
    const container = this.getScrollContainer();
    if (!container) return;

    const scrollTop = container.scrollTop;
    const viewportHeight = container.clientHeight;

    // Skip if container hasn't been laid out yet (would downgrade to fewer items)
    if (viewportHeight === 0) return;

    const totalItems = this.getSortedLocations().length;

    // Calculate visible range
    const firstVisible = Math.floor(scrollTop / ITEM_HEIGHT);
    const visibleCount = Math.ceil(viewportHeight / ITEM_HEIGHT);

    // Add buffer for smooth scrolling
    const newStart = Math.max(0, firstVisible - BUFFER_ITEMS);
    const newEnd = Math.min(totalItems, firstVisible + visibleCount + BUFFER_ITEMS);

    // Only update if bounds actually changed
    if (newStart !== this.virtualStart || newEnd !== this.virtualEnd) {
      this.virtualStart = newStart;
      this.virtualEnd = newEnd;
    }
  }

  /**
   * Scroll to show a selected location only if it's off-screen
   *
   * @remarks
   * Preserves user's scroll position when the selected item is already visible.
   * Only scrolls when the item is completely outside the viewport.
   */
  private scrollToSelected(locationId: number) {
    requestAnimationFrame(() => {
      const sortedLocations = this.getSortedLocations();
      const index = sortedLocations.findIndex(loc => loc.id === locationId);
      const container = this.getScrollContainer();

      if (index === -1 || !container) return;

      // Ensure the item is in the rendered range
      if (index < this.virtualStart || index >= this.virtualEnd) {
        this.virtualStart = Math.max(0, index - BUFFER_ITEMS);
        this.virtualEnd = Math.min(sortedLocations.length, index + BUFFER_ITEMS + 10);
      }

      // Check if item is already visible in viewport
      const itemTop = index * ITEM_HEIGHT;
      const itemBottom = itemTop + ITEM_HEIGHT;
      const viewportTop = container.scrollTop;
      const viewportBottom = viewportTop + container.clientHeight;

      const isVisible = itemTop >= viewportTop && itemBottom <= viewportBottom;
      if (isVisible) return;

      // Scroll to position
      container.scrollTo({
        top: itemTop,
        behavior: "smooth",
      });
    });
  }

  /**
   * Render a single skeleton card matching the actual card structure
   */
  private renderSkeletonCard(index: number) {
    // Vary widths slightly for visual variety
    const nameWidths = ["w-3/4", "w-2/3", "w-4/5", "w-1/2", "w-3/5", "w-5/6"];
    const addressWidths = [
      "w-full",
      "w-5/6",
      "w-full",
      "w-3/4",
      "w-11/12",
      "w-full",
    ];
    const dateCount = [3, 2, 2, 3, 3, 1]; // Number of date badges to show

    const nameWidth = nameWidths[index % nameWidths.length];
    const addressWidth = addressWidths[index % addressWidths.length];
    const numDates = dateCount[index % dateCount.length];

    return html`
      <div class="w-full border-2 border-default rounded-lg p-4 bg-surface">
        <!-- Header: Name & Distance -->
        <div class="flex items-start justify-between mb-2">
          <div class="h-5 ${nameWidth} bg-skeleton rounded-full animate-pulse"></div>
          <div class="h-5 w-16 bg-skeleton rounded-full animate-pulse"></div>
        </div>

        <!-- Address -->
        <div class="h-4 ${addressWidth} bg-skeleton rounded-full animate-pulse mb-1"></div>

        <!-- Available Dates (calendar icon + date badges) -->
        <div class="flex flex-wrap items-center gap-1">
          <!-- Calendar icon skeleton -->
          <div class="h-4 w-4 bg-skeleton rounded-full animate-pulse"></div>
          <!-- Date badge skeletons -->
          ${Array.from({ length: numDates }).map(
            () => html`
              <div class="h-6 w-20 bg-skeleton rounded-full animate-pulse"></div>
            `,
          )}
        </div>
      </div>
    `;
  }

  /**
   * Render loading state with skeleton loaders
   */
  private renderLoading() {
    return html`
      <div class="location-list-scroll-container overflow-y-auto h-full px-4 py-4">
        <div class="space-y-3">
          ${Array.from({ length: 6 }).map((_, index) =>
            this.renderSkeletonCard(index),
          )}
        </div>
      </div>
    `;
  }

  /**
   * Render empty state
   */
  private renderEmpty() {
    return html`
      <div class="flex flex-col items-center justify-center py-12 px-4">
        <svg
          class="w-16 h-16 text-subtle mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <h3 class="text-default mb-2">
          No locations found
        </h3>
        <p class="text-sm text-muted text-center">
          Try adjusting your filters to see more locations
        </p>
      </div>
    `;
  }

  /**
   * Get first future stop date for a location
   */
  private getFirstFutureStopDate(location: Location): Date | null {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const futureStop = location.stops?.find(
      (stop) => new Date(stop.date) >= today
    );
    return futureStop ? new Date(futureStop.date) : null;
  }

  /**
   * Get sorted locations with caching to avoid re-sorting on scroll
   */
  private getSortedLocations(): Location[] {
    // Create cache key from location IDs
    const cacheKey = this.locations.map(l => l.id).join(",");

    if (cacheKey === this.locationsCacheKey) {
      return this.sortedLocationsCache;
    }

    // Sort locations
    const sorted = [...this.locations].sort((a, b) => {
      const dateA = this.getFirstFutureStopDate(a);
      const dateB = this.getFirstFutureStopDate(b);

      // First: locations with upcoming dates before those without
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;

      // Then by distance
      const distA = a.distance ?? Infinity;
      const distB = b.distance ?? Infinity;
      if (distA !== distB) return distA - distB;

      // Then by first future stop date
      if (dateA && dateB) {
        return dateA.getTime() - dateB.getTime();
      }

      return 0;
    });

    // Cache result
    this.sortedLocationsCache = sorted;
    this.locationsCacheKey = cacheKey;

    return sorted;
  }

  /**
   * Render virtualized location cards
   *
   * @remarks
   * Only renders items within virtualStart..virtualEnd range.
   * Uses padding to maintain scroll height for items not rendered.
   */
  private renderLocations() {
    const sortedLocations = this.getSortedLocations();
    const totalItems = sortedLocations.length;

    // Items to render (subset of total)
    const itemsToRender = sortedLocations.slice(this.virtualStart, this.virtualEnd);

    // Calculate padding to maintain scroll position
    const paddingTop = this.virtualStart * ITEM_HEIGHT;
    const paddingBottom = Math.max(0, (totalItems - this.virtualEnd) * ITEM_HEIGHT);

    return html`
      <div
        class="location-list-scroll-container overflow-y-auto h-full px-4 py-1 pb-20 md:pb-4"
      >
        <h2 class="p-0 mb-1">
          ${totalItems > 0 ? `${totalItems} locations` : "No locations"}
        </h2>
        <div
          class="flex flex-col gap-3"
          role="list"
          style="padding-top: ${paddingTop}px; padding-bottom: ${paddingBottom}px;"
        >
          ${repeat(
            itemsToRender,
            (location) => location.id,
            (location) => html`
              <peach-locations-list-item
                .location=${location}
                .selected=${location.id === this.selectedLocationId}
              ></peach-locations-list-item>
            `,
          )}
        </div>
      </div>
    `;
  }

  render() {
    const isEmpty = this.locations.length === 0;

    return html`
      <div class="h-full flex flex-col bg-cream">
        <!-- Content -->
        <div class="flex-1 min-h-0">
          ${
            this.isLoading
              ? this.renderLoading()
              : isEmpty
                ? this.renderEmpty()
                : this.renderLocations()
          }
        </div>
      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-locations-list": LocationsListComponent;
  }
}
